import React, {useEffect, useRef, useState} from 'react';
import CustomerDetail from './CustomerDetail';
import {ReadError} from './CustomerActivity';
import useCustomerRead from './useCustomerRead';
import './CustomerDirectory.css';

const basePath = '/account/subscribers';
const filters = [
  ['all', 'All customers'], ['contactable', 'Contactable'],
  ['active-subscription', 'Active subscription'], ['needs-review', 'Needs review'], ['blocked', 'Blocked'],
];
const sorts = [['recent-activity', 'Recent activity'], ['newest', 'Newest customers'], ['name', 'Name']];

function readLocation(initialCustomerId) {
  const params = new URLSearchParams(window.location.search);
  const match = window.location.pathname.match(/^\/account\/subscribers\/([1-9]\d*)\/?$/);
  return {
    id: match ? match[1] : window.location.pathname === basePath ? null : initialCustomerId,
    query: params.get('query') || '',
    filter: filters.some(([key]) => key === params.get('filter')) ? params.get('filter') : 'all',
    sort: sorts.some(([key]) => key === params.get('sort')) ? params.get('sort') : 'recent-activity',
    page: Math.max(1, parseInt(params.get('page'), 10) || 1),
  };
}

function directoryUrl(state, id = state.id) {
  const params = new URLSearchParams({uiVersion: '2'});
  if (state.query) params.set('query', state.query);
  if (state.filter !== 'all') params.set('filter', state.filter);
  if (state.sort !== 'recent-activity') params.set('sort', state.sort);
  if (state.page > 1) params.set('page', state.page);
  return `${basePath}${id ? `/${encodeURIComponent(id)}` : ''}?${params}`;
}

export default function CustomerDirectory({initialCustomerId = null, isRed = false,
  classicUrl = `${basePath}?uiVersion=1`, helpUrl = '/help/managing-subscriptions'}) {
  const [location, setLocation] = useState(() => readLocation(initialCustomerId));
  const [search, setSearch] = useState(location.query);
  const [restoreId, setRestoreId] = useState(null);
  const listHeading = useRef(null);
  const detailHeading = useRef(null);
  const focusPending = useRef(false);
  const customerLinks = useRef(new Map());
  const list = useCustomerRead(`/v1/seller/customers?${new URLSearchParams({
    query: location.query, filter: location.filter, sort: location.sort,
    page: String(location.page), limit: '25',
  })}`);
  const detail = useCustomerRead(location.id ? `/v1/seller/customers/${location.id}` : null);

  useEffect(() => {
    const pop = () => {
      focusPending.current = true;
      setRestoreId(location.id);
      setLocation(readLocation(initialCustomerId));
    };
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, [initialCustomerId, location.id]);
  useEffect(() => setSearch(location.query), [location.query]);
  useEffect(() => {
    if (!focusPending.current) return;
    const target = location.id ? detailHeading.current
      : customerLinks.current.get(String(restoreId)) || listHeading.current;
    if (target) {
      target.focus();
      focusPending.current = false;
    }
  }, [location.id, detail.data, detail.error, restoreId]);

  const navigate = (patch, focus = false) => {
    const next = {...location, ...patch};
    if (focus) {
      focusPending.current = true;
      setRestoreId(location.id);
    }
    window.history.pushState(null, '', directoryUrl(next));
    setLocation(next);
  };
  const select = (event, id) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate({id: String(id)}, true);
  };
  return <div className={`customer-directory ${isRed ? 'cd-red' : 'cd-marketplace'}${
    location.id ? ' cd-selected' : ''}`}>
    <div className="cd-toolbar">
      <a href={classicUrl}>Classic customer directory</a><a href={helpUrl}>Customer help</a>
      <a href={`${classicUrl}#add-subscriber`}>Add customer in classic tools</a>
      {isRed && <a href={`${classicUrl}#customer-privacy`}>Customer privacy settings</a>}
    </div>
    <div className="cd-layout">
      <div className="cd-list" aria-label="Customer directory">
        <h2 ref={listHeading} tabIndex={-1}>Find a customer</h2>
        <form className="cd-search" onSubmit={event => {
          event.preventDefault();
          navigate({query: search.trim(), page: 1});
        }}>
          <label htmlFor="cd-search">Search customers</label>
          <input id="cd-search" type="search" value={search}
            onChange={event => setSearch(event.target.value)} />
          <button type="submit">Search</button>
          <label htmlFor="cd-filter">Filter customers</label>
          <select id="cd-filter" value={location.filter}
            onChange={event => navigate({filter: event.target.value, page: 1})}>
            {filters.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <label htmlFor="cd-sort">Sort customers</label>
          <select id="cd-sort" value={location.sort}
            onChange={event => navigate({sort: event.target.value, page: 1})}>
            {sorts.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </form>
        {list.loading && <p role="status">Loading customers…</p>}
        {list.error && <ReadError {...list} label="customers" />}
        {list.data && <>
          <p role="status">{list.data.meta.total} customers · Page {list.data.meta.page}</p>
          {!list.data.data.length && <p>No customers match. Try another search or filter.</p>}
          <ul className="cd-customer-list">{list.data.data.map((customer, index) =>
            <li key={customer.id}>
              <a ref={node => {
                if (node) customerLinks.current.set(String(customer.id), node);
                else customerLinks.current.delete(String(customer.id));
              }} href={directoryUrl(location, customer.id)}
              aria-current={String(location.id) === String(customer.id) ? 'page' : undefined}
              onClick={event => select(event, customer.id)} onKeyDown={event => {
                let next;
                if (event.key === 'ArrowDown') next = Math.min(index + 1, list.data.data.length - 1);
                if (event.key === 'ArrowUp') next = Math.max(index - 1, 0);
                if (event.key === 'Home') next = 0;
                if (event.key === 'End') next = list.data.data.length - 1;
                if (next !== undefined) {
                  event.preventDefault();
                  customerLinks.current.get(String(list.data.data[next].id))?.focus();
                }
              }}>
                <strong>{customer.name || 'Customer'}</strong>
                <span>{customer.status?.blocked ? 'Contact blocked'
                  : customer.status?.contactable ? 'Contactable' : 'Email unavailable'}</span>
                <span>{customer.counts.orders} purchased orders ·{' '}
                  {customer.counts.visibleMessages} messages ·{' '}
                  {customer.counts.activeSubscriptions} active subscriptions</span>
              </a>
            </li>)}</ul>
          <div className="cd-pagination" aria-label="Customer pages">
            <button type="button" disabled={location.page <= 1}
              onClick={() => navigate({page: location.page - 1})}>Previous customers</button>
            <button type="button" disabled={!list.data.meta.hasMore}
              onClick={() => navigate({page: location.page + 1})}>Next customers</button>
          </div>
        </>}
      </div>
      <div className="cd-detail" aria-label="Selected customer">
        {location.id ? <>
          <button type="button" className="cd-back"
            onClick={() => navigate({id: null}, true)}>Back to Customers</button>
          {detail.loading && <p role="status">Loading customer…</p>}
          {detail.error && <>
            <h2 ref={detailHeading} tabIndex={-1}>Customer unavailable</h2>
            <ReadError {...detail} label="customer" />
            <a href={`${basePath}/${location.id}?uiVersion=1`}>Open classic customer tools</a>
          </>}
          {detail.data && <CustomerDetail key={location.id} customer={detail.data.data}
            headingRef={detailHeading} />}
        </> : <p>Select a customer to see their history.</p>}
      </div>
    </div>
  </div>;
}
